import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  CircularProgress,
  Alert,
  Typography,
  Stack,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useSnackbar } from 'notistack';
import cloudApi from '../../../app/api/cloudApi';
import { useAvailableActions } from '../../../hooks/useAvailableActions';

const DEFAULT_OPEN_TIME_SECONDS = 3;
const QUEUED_REFRESH_DELAY_MS = 3000;

export default function RelaySettingsTab({ deviceId, siteId, accountId, token }) {
  const { enqueueSnackbar } = useSnackbar();
  const { canEditSites } = useAvailableActions();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pulsing, setPulsing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const [selectedDoorIds, setSelectedDoorIds] = useState([]);
  const [deleteAllDoors, setDeleteAllDoors] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const [config, setConfig] = useState({
    isActive: false,
    openTimeSeconds: DEFAULT_OPEN_TIME_SECONDS,
    name: 'Device Relay',
  });
  const [pulseDurationSeconds, setPulseDurationSeconds] = useState(DEFAULT_OPEN_TIME_SECONDS);

  const loadStatus = useCallback(async () => {
    if (!deviceId || !token) return;

    setLoading(true);
    setError(null);
    try {
      const response = await cloudApi.getRelayStatus(token, siteId, accountId, deviceId);
      const data = response.data;
      setStatus(data);
      setSelectedDoorIds([]);

      const relayDoor = data?.doors?.find((d) => d.isRelayActive) ?? data?.doors?.[0];
      setConfig({
        isActive: data?.deviceRelayDoorConfigured ?? false,
        openTimeSeconds:
          relayDoor?.extendedDoorOpenTime ||
          relayDoor?.autoLockTimeout ||
          DEFAULT_OPEN_TIME_SECONDS,
        name: relayDoor?.name || 'Device Relay',
      });
    } catch (err) {
      console.error('Error loading relay status:', err);
      const errorMessage = err.response?.data || err.message || 'Failed to load relay status';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [deviceId, token, siteId, accountId, enqueueSnackbar]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const scheduleRefresh = () => {
    setTimeout(() => {
      loadStatus();
    }, QUEUED_REFRESH_DELAY_MS);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await cloudApi.configureRelay(token, siteId, accountId, {
        deviceId,
        isActive: config.isActive,
        openTimeSeconds: config.isActive ? config.openTimeSeconds : null,
        name: config.name || null,
      });
      enqueueSnackbar('Relay configuration queued — refreshing status shortly', { variant: 'success' });
      scheduleRefresh();
    } catch (err) {
      console.error('Error saving relay config:', err);
      const errorMessage = err.response?.data || err.message || 'Failed to save relay configuration';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handlePulse = async () => {
    setPulsing(true);
    setError(null);
    try {
      await cloudApi.pulseRelay(token, siteId, accountId, deviceId, pulseDurationSeconds || null);
      enqueueSnackbar('Relay pulse queued', { variant: 'success' });
      scheduleRefresh();
    } catch (err) {
      console.error('Error pulsing relay:', err);
      const errorMessage = err.response?.data || err.message || 'Failed to pulse relay';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setPulsing(false);
    }
  };

  const handleToggleDoor = (doorId) => {
    setDeleteAllDoors(false);
    setSelectedDoorIds((prev) =>
      prev.includes(doorId) ? prev.filter((id) => id !== doorId) : [...prev, doorId]
    );
  };

  const handleToggleDeleteAll = (checked) => {
    setDeleteAllDoors(checked);
    if (checked) {
      setSelectedDoorIds([]);
    }
  };

  const handleDeleteDoors = async () => {
    setDeleteConfirmOpen(false);
    setDeleting(true);
    setError(null);
    try {
      const doorIds = deleteAllDoors ? null : selectedDoorIds;
      await cloudApi.deleteFaceScannerDoors(token, siteId, accountId, deviceId, doorIds);
      enqueueSnackbar('Door removal queued — refreshing status shortly', { variant: 'success' });
      setSelectedDoorIds([]);
      setDeleteAllDoors(false);
      scheduleRefresh();
    } catch (err) {
      console.error('Error deleting doors:', err);
      const errorMessage = err.response?.data || err.message || 'Failed to delete doors';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const openDeleteConfirm = () => {
    if (!deleteAllDoors && selectedDoorIds.length === 0) {
      enqueueSnackbar('Select doors to remove or choose delete all', { variant: 'warning' });
      return;
    }
    setDeleteConfirmOpen(true);
  };

  if (loading && !status) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const doors = status?.doors ?? [];

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack spacing={3}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h6">Relay Status</Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Chip
              label={status?.deviceRelayDoorConfigured ? 'Relay configured' : 'Relay not configured'}
              color={status?.deviceRelayDoorConfigured ? 'success' : 'default'}
              size="small"
            />
            <Button variant="outlined" size="small" onClick={loadStatus} disabled={loading}>
              Refresh
            </Button>
          </Box>
        </Box>

        {status?.note && (
          <Alert severity="info">{status.note}</Alert>
        )}

        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                {canEditSites && <TableCell padding="checkbox" />}
                <TableCell>Door ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Relay Device</TableCell>
                <TableCell>Port</TableCell>
                <TableCell>Open Time (s)</TableCell>
                <TableCell>Active</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {doors.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canEditSites ? 7 : 6} align="center">
                    No doors on device
                  </TableCell>
                </TableRow>
              ) : (
                doors.map((door) => (
                  <TableRow key={door.doorId}>
                    {canEditSites && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedDoorIds.includes(door.doorId)}
                          onChange={() => handleToggleDoor(door.doorId)}
                          disabled={deleteAllDoors || deleting}
                        />
                      </TableCell>
                    )}
                    <TableCell>{door.doorId}</TableCell>
                    <TableCell>{door.name || '—'}</TableCell>
                    <TableCell>{door.relayDeviceId || '—'}</TableCell>
                    <TableCell>{door.relayPort}</TableCell>
                    <TableCell>{door.extendedDoorOpenTime || door.autoLockTimeout || 0}</TableCell>
                    <TableCell>
                      <Chip
                        label={door.isRelayActive ? 'Yes' : 'No'}
                        color={door.isRelayActive ? 'success' : 'default'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider />

        <Typography variant="h6">Relay Configuration</Typography>

        <FormControlLabel
          control={
            <Switch
              checked={config.isActive}
              onChange={(e) => setConfig((prev) => ({ ...prev, isActive: e.target.checked }))}
              disabled={!canEditSites}
            />
          }
          label="Enable device relay"
        />

        <TextField
          fullWidth
          type="number"
          label="Open time (seconds)"
          value={config.openTimeSeconds}
          onChange={(e) =>
            setConfig((prev) => ({
              ...prev,
              openTimeSeconds: parseInt(e.target.value, 10) || DEFAULT_OPEN_TIME_SECONDS,
            }))
          }
          disabled={!canEditSites || !config.isActive}
          helperText="How long the relay stays open after successful authentication"
          inputProps={{ min: 1, max: 60 }}
        />

        <TextField
          fullWidth
          label="Door name"
          value={config.name}
          onChange={(e) => setConfig((prev) => ({ ...prev, name: e.target.value }))}
          disabled={!canEditSites}
          helperText="Name stored on the device (default: Device Relay)"
        />

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={loadStatus} disabled={loading || saving}>
            Reset
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!canEditSites || loading || saving}
            startIcon={saving ? <CircularProgress size={20} /> : <Icon icon="solar:diskette-bold" />}
          >
            {saving ? 'Saving...' : 'Save Configuration'}
          </Button>
        </Box>

        <Divider />

        <Typography variant="h6">Test Relay</Typography>

        <TextField
          fullWidth
          type="number"
          label="Pulse duration (seconds)"
          value={pulseDurationSeconds}
          onChange={(e) =>
            setPulseDurationSeconds(parseInt(e.target.value, 10) || DEFAULT_OPEN_TIME_SECONDS)
          }
          disabled={!canEditSites}
          helperText="Leave default to use site configuration value"
          inputProps={{ min: 1, max: 60 }}
        />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={handlePulse}
            disabled={!canEditSites || pulsing}
            startIcon={pulsing ? <CircularProgress size={20} /> : <Icon icon="solar:bolt-bold" />}
          >
            {pulsing ? 'Pulsing...' : 'Pulse Relay'}
          </Button>
        </Box>

        {canEditSites && doors.length > 0 && (
          <>
            <Divider />

            <Typography variant="h6">Remove Doors</Typography>

            <Alert severity="warning">
              Removing doors from the device cannot be undone from the cloud. Operations are queued on the box.
            </Alert>

            <FormControlLabel
              control={
                <Checkbox
                  checked={deleteAllDoors}
                  onChange={(e) => handleToggleDeleteAll(e.target.checked)}
                  disabled={deleting}
                />
              }
              label="Remove all doors from device"
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                color="error"
                onClick={openDeleteConfirm}
                disabled={deleting || (!deleteAllDoors && selectedDoorIds.length === 0)}
                startIcon={deleting ? <CircularProgress size={20} /> : <Icon icon="solar:trash-bin-trash-bold" />}
              >
                {deleting ? 'Removing...' : 'Remove Doors'}
              </Button>
            </Box>
          </>
        )}
      </Stack>

      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm door removal</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {deleteAllDoors
              ? 'Remove all doors from this device? This operation is queued on the box.'
              : `Remove ${selectedDoorIds.length} selected door(s) from this device? This operation is queued on the box.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteDoors} color="error" variant="contained">
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
